/**
 * Checkout Hook using TanStack Query
 * Replaces the coordinator pattern with automatic cache invalidation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutResource } from '../../core/resources/checkout';
import { getAssignedOrderBumpOfferIds } from '../../core/funnelClient';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { useCurrency } from './useCurrency';
import { usePluginConfig } from './usePluginConfig';
const DEFAULT_MESSAGES = {
    sessionTimeout: 'Session initialization timeout. Please refresh the page and try again.',
    initFailed: 'Failed to initialize checkout resource',
    noCheckoutSession: 'No checkout session available',
};
export function useCheckoutQuery(options = {}) {
    const { checkoutToken: providedToken, enabled = true, messages: userMessages } = options;
    const messages = { ...DEFAULT_MESSAGES, ...userMessages };
    const { storeId } = usePluginConfig();
    const currency = useCurrency();
    const queryClient = useQueryClient();
    const { isSessionInitialized, session, client } = useTagadaContext();
    // Track pending session promises to avoid creating multiple promises
    const pendingSessionPromise = useRef(null);
    const sessionResolvers = useRef(new Set());
    // Resolve all pending promises when session becomes ready
    useEffect(() => {
        if (isSessionInitialized && sessionResolvers.current.size > 0) {
            console.log('✅ [useCheckout] CMS session ready, resolving pending promises');
            sessionResolvers.current.forEach(resolve => resolve());
            sessionResolvers.current.clear();
            pendingSessionPromise.current = null;
        }
    }, [isSessionInitialized]);
    // Clean, event-driven session waiting - no polling needed
    const waitForSession = useCallback(async () => {
        if (isSessionInitialized) {
            return Promise.resolve();
        }
        // Reuse existing promise if one is pending
        if (pendingSessionPromise.current) {
            return pendingSessionPromise.current;
        }
        console.log('⏳ [useCheckout] Waiting for CMS session to be initialized...');
        // Create new promise that will be resolved by useEffect above
        pendingSessionPromise.current = new Promise((resolve, reject) => {
            // Add resolver to set for useEffect to call
            sessionResolvers.current.add(resolve);
            // Safety timeout
            setTimeout(() => {
                if (sessionResolvers.current.has(resolve)) {
                    sessionResolvers.current.delete(resolve);
                    if (sessionResolvers.current.size === 0) {
                        pendingSessionPromise.current = null;
                    }
                    reject(new Error(messages.sessionTimeout));
                }
            }, 10000);
        });
        return pendingSessionPromise.current;
    }, [isSessionInitialized]);
    // Create checkout resource client
    const checkoutResource = useMemo(() => {
        try {
            return new CheckoutResource(getGlobalApiClient(), client.bus);
        }
        catch (error) {
            throw new Error(messages.initFailed + ': ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, [client.bus]);
    // Internal token state that can be updated after init
    const [internalToken, setInternalToken] = useState(providedToken);
    // Update internal token when provided token changes
    useEffect(() => {
        if (providedToken && providedToken !== internalToken) {
            setInternalToken(providedToken);
        }
    }, [providedToken, internalToken]);
    // Use provided token or internal token
    const checkoutToken = providedToken || internalToken;
    // Only send currency to the backend when explicitly set via URL param or persisted storage.
    // When not explicit, the backend uses the session's own selectedPresentmentCurrency --
    // the frontend then reads the currency from the response, never guesses.
    const explicitCurrency = currency.isExplicit ? currency.code : undefined;
    // Main checkout query
    const { data: checkout, isLoading, error, isSuccess, refetch, } = useQuery({
        queryKey: ['checkout', checkoutToken, explicitCurrency],
        queryFn: () => checkoutResource.getCheckout(checkoutToken, explicitCurrency),
        enabled: enabled && !!checkoutToken && isSessionInitialized,
        staleTime: 30000, // 30 seconds
        refetchOnWindowFocus: false,
    });
    // Refresh function
    const refresh = useCallback(async () => {
        if (checkoutToken) {
            await refetch();
        }
    }, [refetch, checkoutToken]);
    // Initialize checkout mutation (async mode for fast response)
    const initMutation = useMutation({
        mutationFn: async (params) => {
            const enabledOrderBumpOfferIds = params.enabledOrderBumpOfferIds ?? getAssignedOrderBumpOfferIds();
            const requestBody = {
                ...params,
                storeId: params.storeId || storeId,
                returnUrl: params.returnUrl || window.location.origin,
                customerId: params.customerId || session?.customerId,
                customer: {
                    ...params.customer,
                    currency: params.customer?.currency ?? currency.code,
                },
                ...(enabledOrderBumpOfferIds && { enabledOrderBumpOfferIds }),
            };
            // Use async mode for fast response (~50ms vs 2-5s)
            const asyncResponse = await checkoutResource.initCheckoutAsync(requestBody);
            return {
                checkoutToken: asyncResponse.checkoutToken,
                checkoutSession: {}, // Will be populated when getCheckout is called
            };
        },
        onSuccess: (response) => {
            // Update URL with checkout token
            if (typeof window !== 'undefined' && response.checkoutToken) {
                const currentUrl = new URL(window.location.href);
                if (!currentUrl.searchParams.has('checkoutToken')) {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('checkoutToken', response.checkoutToken);
                    window.history.replaceState(null, '', newUrl.toString());
                }
            }
        },
    });
    // Order bump functionality removed - use useOrderBumpQuery instead
    // Replace session line items mutation (full rewrite)
    const replaceSessionLineItemsMutation = useMutation({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.replaceSessionLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Line items mutation with optimistic updates
    const lineItemsMutation = useMutation({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.updateLineItems(checkout.checkoutSession.id, lineItems);
        },
        onMutate: async ({ lineItems }) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['checkout', checkoutToken] });
            // Snapshot the previous value
            const previousCheckout = queryClient.getQueryData(['checkout', checkoutToken]);
            // Optimistically update the checkout data
            if (previousCheckout && checkout?.checkoutSession?.id) {
                const optimisticCheckout = {
                    ...previousCheckout,
                    checkoutSession: {
                        ...previousCheckout.checkoutSession,
                        sessionLineItems: lineItems.map(item => ({
                            variantId: item.variantId,
                            quantity: item.quantity,
                            priceId: item.priceId,
                            isOrderBump: false, // Default for line items
                        })),
                    },
                };
                queryClient.setQueryData(['checkout', checkoutToken], optimisticCheckout);
            }
            // Return a context object with the snapshotted value
            return { previousCheckout };
        },
        onError: (err, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context && typeof context === 'object' && 'previousCheckout' in context) {
                queryClient.setQueryData(['checkout', checkoutToken], context.previousCheckout);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure we have the latest data
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Add line items mutation
    const addLineItemsMutation = useMutation({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.addLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Remove line items mutation
    const removeLineItemsMutation = useMutation({
        mutationFn: ({ lineItems }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.removeLineItems(checkout.checkoutSession.id, lineItems);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Item quantity mutation
    const quantityMutation = useMutation({
        mutationFn: ({ variantId, quantity, priceId }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.setItemQuantity(checkout.checkoutSession.id, variantId, quantity, priceId);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Customer update mutation
    const customerMutation = useMutation({
        mutationFn: (data) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.updateCustomer(checkout.checkoutSession.id, data);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Customer and session info update mutation
    const customerAndSessionMutation = useMutation({
        mutationFn: (data) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.updateCustomerAndSessionInfo(checkout.checkoutSession.id, data);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
        },
    });
    // Promotion code mutation
    const promotionMutation = useMutation({
        mutationFn: ({ code }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.applyPromotionCode(checkout.checkoutSession.id, code);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
            if (checkout?.checkoutSession?.id) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkout.checkoutSession.id] });
            }
        },
    });
    // Remove promotion mutation
    const removePromotionMutation = useMutation({
        mutationFn: ({ promotionId }) => {
            if (!checkout?.checkoutSession?.id) {
                throw new Error(messages.noCheckoutSession);
            }
            return checkoutResource.removePromotion(checkout.checkoutSession.id, promotionId);
        },
        onSuccess: () => {
            if (checkoutToken) {
                void queryClient.invalidateQueries({ queryKey: ['checkout', checkoutToken] });
            }
            if (checkout?.checkoutSession?.id) {
                void queryClient.invalidateQueries({ queryKey: ['promotions', checkout.checkoutSession.id] });
            }
        },
    });
    const previewCheckoutSessionMutation = useMutation({
        mutationFn: ({ lineItems, promotionIds }) => checkoutResource.previewCheckoutSession({
            lineItems,
            storeId,
            currency: currency.code,
            promotionIds,
        }),
    });
    // Stable mutator wrappers. Returning inline arrows each render produced new
    // refs on every checkout state change (mutation success → query invalidate
    // → refetch → re-render), which propagated instability into consumer
    // useCallback/useEffect deps and broke debounced auto-update (the timer in
    // the shared RHF watch listener kept getting cleared mid-debounce, so the
    // customer-and-session-info call never fired for email-only typing).
    const init = useCallback(async (params) => {
        await waitForSession();
        const result = await initMutation.mutateAsync(params);
        setInternalToken(result.checkoutToken);
        return {
            checkoutSession: checkout?.checkoutSession ?? {},
            checkoutToken: result.checkoutToken,
        };
    }, [waitForSession, initMutation.mutateAsync, checkout?.checkoutSession]);
    const replaceSessionLineItems = useCallback((lineItems) => replaceSessionLineItemsMutation.mutateAsync({ lineItems }), [replaceSessionLineItemsMutation.mutateAsync]);
    const updateLineItems = useCallback((lineItems) => lineItemsMutation.mutateAsync({ lineItems }), [lineItemsMutation.mutateAsync]);
    const updateLineItemsOptimistic = useCallback((lineItems) => lineItemsMutation.mutate({ lineItems }), [lineItemsMutation.mutate]);
    const addLineItems = useCallback((lineItems) => addLineItemsMutation.mutateAsync({ lineItems }), [addLineItemsMutation.mutateAsync]);
    const removeLineItems = useCallback((lineItems) => removeLineItemsMutation.mutateAsync({ lineItems }), [removeLineItemsMutation.mutateAsync]);
    const setItemQuantity = useCallback((variantId, quantity, priceId) => quantityMutation.mutateAsync({ variantId, quantity, priceId }), [quantityMutation.mutateAsync]);
    const updateCustomer = useCallback((data) => customerMutation.mutateAsync(data), [customerMutation.mutateAsync]);
    const updateCustomerAndSessionInfo = useCallback((data) => customerAndSessionMutation.mutateAsync(data), [customerAndSessionMutation.mutateAsync]);
    const applyPromotionCode = useCallback((code) => promotionMutation.mutateAsync({ code }), [promotionMutation.mutateAsync]);
    const removePromotion = useCallback((promotionId) => removePromotionMutation.mutateAsync({ promotionId }), [removePromotionMutation.mutateAsync]);
    const previewCheckoutSession = useCallback((lineItems, promotionIds) => previewCheckoutSessionMutation.mutateAsync({ lineItems, promotionIds }), [previewCheckoutSessionMutation.mutateAsync]);
    return {
        // Query data
        checkout,
        isLoading,
        error,
        isSuccess,
        // Actions
        init,
        refresh,
        // Checkout operations
        replaceSessionLineItems,
        updateLineItems,
        updateLineItemsOptimistic,
        addLineItems,
        removeLineItems,
        setItemQuantity,
        updateCustomer,
        updateCustomerAndSessionInfo,
        applyPromotionCode,
        removePromotion,
        previewCheckoutSession,
    };
}
